import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../lib/api';

/** Persist a session id for the lifetime of the tab so the founder admin can
 * see "unique sessions today". Cleared when the user closes all tabs. */
const SESSION_KEY = 'mbap_session_id';
const getSessionId = () => {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
};

const trackEventRaw = (payload) => {
  // Best-effort. Never blocks the UI. Server is fault-tolerant.
  // We use `navigator.sendBeacon` when available so the request still goes
  // out even when the user immediately navigates away (clicks a CTA that
  // routes to /signup) — otherwise the fetch gets cancelled by React Router
  // before the request lands on the server, which is why CTA clicks were
  // showing 0 in the founder dashboard despite real clicks happening.
  try {
    const url = `${(api.defaults.baseURL || '').replace(/\/$/, '')}/track/event`;
    const body = JSON.stringify(payload);
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // Beacon uses Blob with text/plain to avoid CORS preflight; FastAPI
      // parses request.body() in /track/event so any content-type works.
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) return null;
    }
    return api.post('/track/event', payload, { timeout: 4000 }).catch(() => null);
  } catch {
    return null;
  }
};

/** Track a CTA click from anywhere in the app. */
export const trackCta = (label, extra) => {
  trackEventRaw({
    type: 'cta_click',
    label,
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    sessionId: getSessionId(),
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    extra,
  });
};

/** Track an arbitrary product event (signup_start, checkout_start, etc.). */
export const trackEvent = (type, payload = {}) => {
  trackEventRaw({
    type,
    sessionId: getSessionId(),
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    ...payload,
  });
};

/** Mounts a router-aware page-view tracker. Drop into App.jsx once. */
const useAnalytics = () => {
  const location = useLocation();
  const lastPath = useRef(null);
  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastPath.current === path) return;
    lastPath.current = path;
    trackEventRaw({
      type: 'page_view',
      path,
      sessionId: getSessionId(),
      referrer: typeof document !== 'undefined' ? document.referrer : '',
    });
  }, [location.pathname, location.search]);
};

export default useAnalytics;
